import { SIDES } from './definitions'
import { deepCopy } from './algorithm/utils'

type ResultValue = number | string | null
type ResultCandidate = {
	me: Array<ResultValue | undefined>
	them: Array<ResultValue | undefined>
}

type ValidateResultProps = {
	result: Result
	side: Side
	qtr: number
}

type ValidateResultOptions = {
	requireFiniteNumber?: boolean
}

export const RESULT_CODES = [
	{ label: 'Normal', code: 'normal', value: 0 },
	{ label: 'Forfeit Win', code: 'forfeitWin', value: -1 },
	{ label: 'Forfeit Loss', code: 'forfeitLoss', value: -2 },
	{ label: 'Abandoned', code: 'abandoned', value: -3 },
]

export function queryResultCode(result: Result | undefined) {
	const codeNumber = result?.me[0] ?? 0
	const obj = RESULT_CODES.find(obj => obj.value === codeNumber) || RESULT_CODES[0]
	return {
		codeString: obj.code,
		codeNumber: obj.value,
		codeLabel: obj.label,
	}
}

export function resultCodeStringToCodeNumber(codeString: string) {
	const code = RESULT_CODES.find(obj => obj.code === codeString)
	return code?.value ?? 0
}

// format 'result' field
export function formatResult(result: Result | undefined, numPeriods = 4): Result {

	//
	// Ensure basic format is correct
	//
	if (!result || typeof result !== 'object' || !result.me || !result.them || result.me.length !== numPeriods || result.them.length !== numPeriods) {
		return {
			me:		new Array(numPeriods).fill(null),
			them:	new Array(numPeriods).fill(null),
		}
	}

	const r = deepCopy(result)

	SIDES.forEach(side => {
		[ ...Array(numPeriods).keys() ].forEach(qtrKey => {
			const qtr = Number(qtrKey)
			const value = r[side][qtr] as ResultValue

			if (value === '') {
				r[side][qtr] = null
			} else if (value !== null) {
				r[side][qtr] = Number(value)
			}
		})
	})

	return r
}

//
// Check whether this result value makes sense.
//
export function validateResult(props: ValidateResultProps, options: ValidateResultOptions = { }): boolean {
	const { result, side, qtr } = props

    // console.log('validateResult', result, side, qtr)

	if (!side || !result[side]) {
		// Not sure how this happens, but it happens
		return false
	}

	const value = result[side][qtr]
	const otherSide = side === 'me' ? 'them' : 'me'

	// console.log('validateResult', value, side, qtr, result)

	if (value === undefined) {
		// Must be null, not undefined or empty -- this is
		// to catch results like [ 1, 2, empty, 4 ]
		return false
	}

	if (value === null) {

        // Both sides must have value
		if (result[otherSide][qtr] !== null) {
			return false
		}

		// Can't be null if previous qtr is not null
		if (qtr > 0 && result[side][qtr - 1] !== null) {
			return false
		}

		return true
	}

	const numericValue = Number(value)

	if (options.requireFiniteNumber && !Number.isFinite(numericValue)) {
		return false
	}

    // Can't be less than 0
    if (numericValue < 0) {
        return false
    }

    // Can't be more than 999
    if (numericValue > 999) {
        return false
    }

	// Must be higher than prev values
	for (let i = qtr - 1; i >= 0; i -= 1) {
		if (numericValue < Number(result[side][i])) {
			return false
		}
	}

	return true
}

//
// This is sent an entire 'results' object (by EditItem.js -> formIsInvalid()),
// which we should check properly.
//
export function isValidResult(value: Result | undefined, numPeriods: number, allowSpecialCodes: boolean = true) {
	console.log('isValidResult() value:', value, numPeriods, allowSpecialCodes)

	return isValidResultStructure(value, numPeriods, {
		allowSpecialCodes,
		requireFiniteNumbers: false,
	})
}

function isValidNormalizedResult(value: Result | undefined, numPeriods: number) {
	return isValidResultStructure(value, numPeriods, {
		allowSpecialCodes: false,
		requireFiniteNumbers: true,
	})
}

function isValidResultStructure(
	value: Result | undefined,
	numPeriods: number,
	options: {
		allowSpecialCodes: boolean
		requireFiniteNumbers: boolean
	},
) {
	if (!value?.me || !value?.them) {
		return false
	}

	// Negative number in the first 'me' result is a special code
	if (options.allowSpecialCodes) {
		const { codeNumber } = queryResultCode(value)
		if (codeNumber) {
			return true
		}
	}

	const permitAllNull = true

	//
	// Return true if we should save this 'result' value,
	// or false if we should consider it corrupt and delete it.
	//
	let ok = true
	let allNull = true

	SIDES.forEach(side => {
		if (value[side].length > numPeriods) {
			ok = false
		} else {
			const qtrs = new Array(numPeriods).fill(null)
			qtrs.forEach((_, qtr) => {
				if (ok && !validateResult({
					result: value,
					side,
					qtr,
				}, {
					requireFiniteNumber: options.requireFiniteNumbers,
				})) {
					ok = false
				}
				if (allNull && value[side][qtr] !== null) {
					allNull = false
				}
			})
		}
	})

	// console.log('Games.js isInvalid() ok?', ok)
	if (ok && allNull && !permitAllNull) {
		// console.log("Would have been ok but allNull is true and permitAllNull is false")
		ok = false
	}

	// console.log('isValidResult() returning', ok)
	return ok
}

function hasCompleteScoreBeforeFinal(result: ResultCandidate, numPeriods: number) {
	return [ ...Array(Math.max(numPeriods - 1, 0)).keys() ].some(qtr =>
		result.me[qtr] !== null &&
		result.me[qtr] !== undefined &&
		result.them[qtr] !== null &&
		result.them[qtr] !== undefined
	)
}

export function hasDetailedPeriodScores(result: Result | undefined, numPeriods: number) {
	const formatted = formatResult(result, numPeriods)

	if (!isValidNormalizedResult(formatted, numPeriods)) {
		return false
	}

	return hasCompleteScoreBeforeFinal(formatted, numPeriods)
}
